import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconChart, IconClock, IconClose, IconHome, IconKey, IconLink, IconShield, IconUser } from '../../components/icons';
import {
  StatusBanner,
  panelClassName,
  inputClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';
import DashboardShell from '../layout/DashboardShell';
import { getAdmins, getSuperAdminAnalytics, getSuperAdminPersonalizedAnalytics, updateAdminStatus } from './authApi';
import AnalyticsPage from '../analytics/AnalyticsPage';

const ADMINS_PAGE_SIZE = 25;

function AccountStatusBadge({ isActive }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
      <span
        className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`}
        aria-hidden="true"
      />
      <span className={isActive ? 'text-emerald-800' : 'text-red-800'}>
        {isActive ? 'Active' : 'Blocked'}
      </span>
    </span>
  );
}

const OVERVIEW_TONES = {
  blue: {
    card: 'border-blue-500/20 bg-[linear-gradient(145deg,rgb(29_78_216/0.08),rgb(var(--crm-surface))_58%)]',
    icon: 'bg-blue-500/12 text-blue-700',
    bar: 'bg-blue-600',
    glow: 'from-blue-400/20',
  },
  emerald: {
    card: 'border-emerald-500/25 bg-[linear-gradient(145deg,rgb(16_185_129/0.1),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-emerald-500/15 text-emerald-700',
    bar: 'bg-emerald-500',
    glow: 'from-emerald-400/20',
  },
  amber: {
    card: 'border-amber-500/25 bg-[linear-gradient(145deg,rgb(245_158_11/0.12),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-amber-500/15 text-amber-700',
    bar: 'bg-amber-500',
    glow: 'from-amber-400/20',
  },
  rose: {
    card: 'border-rose-500/20 bg-[linear-gradient(145deg,rgb(244_63_94/0.08),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-rose-500/12 text-rose-700',
    bar: 'bg-rose-500',
    glow: 'from-rose-400/15',
  },
  violet: {
    card: 'border-zinc-400/30 bg-[linear-gradient(145deg,rgb(113_113_122/0.08),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-zinc-500/12 text-zinc-700',
    bar: 'bg-zinc-500',
    glow: 'from-zinc-400/15',
  },
  slate: {
    card: 'border-stone-300/80 bg-[linear-gradient(145deg,rgb(120_113_108/0.08),rgb(var(--crm-surface))_55%)]',
    icon: 'bg-stone-500/12 text-stone-700',
    bar: 'bg-stone-500',
    glow: 'from-stone-400/15',
  },
};

function OverviewMetricCard({ label, value, detail, icon: Icon, tone = 'blue' }) {
  const styles = OVERVIEW_TONES[tone] || OVERVIEW_TONES.blue;
  return (
    <article
      className={`relative overflow-hidden rounded-2xl border p-5 shadow-[0_10px_30px_-24px_rgb(24_24_27_/_0.35)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgb(24_24_27_/_0.4)] ${styles.card}`}
    >
      <div
        className={`pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${styles.glow} to-transparent blur-2xl`}
        aria-hidden="true"
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500">
            {label}
          </span>
          <strong className="mt-2.5 block truncate font-display text-3xl font-extrabold tracking-tight text-zinc-900">
            {value}
          </strong>
          {detail ? <p className="mt-2 text-[13px] leading-relaxed text-zinc-500">{detail}</p> : null}
        </div>
        {Icon ? (
          <span className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${styles.icon}`} aria-hidden="true">
            <Icon className="h-5 w-5" />
          </span>
        ) : null}
      </div>
      <div className={`mt-4 h-1 w-12 rounded-full ${styles.bar}`} aria-hidden="true" />
    </article>
  );
}

function AccountStatusToggle({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={[
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition',
        'disabled:cursor-not-allowed disabled:opacity-60',
        checked
          ? 'border-emerald-500 bg-emerald-500'
          : 'border-slate-300 bg-slate-300',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-5 w-5 rounded-full bg-white shadow transition',
          checked ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}

function AdminDetailsModal({ admin, onClose, onStatusChange, updating }) {
  if (!admin) {
    return null;
  }

  const initials = admin.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const marketplaces = Array.isArray(admin.spApiMarketplaces)
    ? admin.spApiMarketplaces.join(', ')
    : '';

  const contactFields = [
    { label: 'Email address', value: admin.email },
    { label: 'Phone number', value: admin.phone || 'Not provided' },
  ];

  const spApiFields = [
    {
      label: 'SP-API status',
      value: admin.hasSpApiConfigured ? 'Connected' : 'Pending setup',
      tone: admin.hasSpApiConfigured ? 'success' : 'warning',
    },
    {
      label: 'SP-API expires',
      value: admin.spApiExpiresAt
        ? new Date(admin.spApiExpiresAt).toLocaleDateString()
        : 'Not set',
    },
    {
      label: 'Seller ID',
      value: admin.spApiSellerId || 'Not set',
    },
    {
      label: 'Marketplaces',
      value: marketplaces || (admin.hasSpApiConfigured ? 'Not set' : '—'),
    },
  ];

  const activityFields = [
    { label: 'Registration IP', value: admin.ipAddress || 'Unknown' },
    { label: 'Joined', value: new Date(admin.createdAt).toLocaleDateString() },
    {
      label: 'Last active',
      value: admin.lastActiveAt
        ? new Date(admin.lastActiveAt).toLocaleString()
        : 'Never recorded',
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={updating ? undefined : onClose}
    >
      <div
        className="crm-panel w-full max-w-2xl overflow-hidden shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-details-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[rgb(var(--crm-border))] bg-[linear-gradient(135deg,rgb(var(--crm-surface)),rgb(var(--crm-accent-soft)/0.4))] px-6 py-5">
          <div className="flex min-w-0 items-center gap-4">
            <span
              className={[
                'grid h-12 w-12 shrink-0 place-items-center rounded-xl text-sm font-bold',
                admin.isActive
                  ? 'bg-[rgb(var(--crm-accent-soft))] text-[rgb(var(--crm-accent-text))]'
                  : 'bg-slate-100 text-slate-500',
              ].join(' ')}
            >
              {initials}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2
                  id="admin-details-title"
                  className="truncate text-xl font-semibold text-[rgb(var(--crm-text))]"
                >
                  {admin.name}
                </h2>
                <AccountStatusBadge isActive={admin.isActive} />
              </div>
              <p className="mt-1 truncate text-sm text-[rgb(var(--crm-text-muted))]">
                {admin.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="crm-btn-secondary !min-h-9 !px-2.5"
            onClick={onClose}
            disabled={updating}
            aria-label="Close admin details"
          >
            <IconClose className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-5 px-6 py-5">
          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--crm-text-muted))]">
              Contact
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {contactFields.map((field) => (
                <DetailField key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--crm-text-muted))]">
              SP-API
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {spApiFields.map((field) => (
                <DetailField
                  key={field.label}
                  label={field.label}
                  value={field.value}
                  tone={field.tone}
                />
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--crm-text-muted))]">
              Activity
            </h3>
            <div className="grid gap-3 sm:grid-cols-3">
              {activityFields.map((field) => (
                <DetailField key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-[rgb(var(--crm-text))]">Account access</p>
            <p className="mt-0.5 text-xs text-[rgb(var(--crm-text-muted))]">
              {admin.isActive
                ? 'Account is active. Turn off to block login (data is kept).'
                : 'Account is blocked. Turn on to restore login access.'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`text-xs font-semibold ${
                admin.isActive ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              {updating ? 'Saving…' : admin.isActive ? 'On' : 'Off'}
            </span>
            <AccountStatusToggle
              checked={Boolean(admin.isActive)}
              disabled={updating}
              label={admin.isActive ? 'Block account' : 'Unblock account'}
              onChange={() => onStatusChange(admin)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value, tone }) {
  const valueClass =
    tone === 'success'
      ? 'text-emerald-800'
      : tone === 'warning'
        ? 'text-amber-800'
        : 'text-[rgb(var(--crm-text))]';

  return (
    <div className="rounded-xl border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-surface))] px-4 py-3">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
        {label}
      </span>
      <strong className={`mt-1.5 block break-words text-sm font-semibold ${valueClass}`}>
        {value}
      </strong>
    </div>
  );
}

function SuperAdminDashboard({ token, user, onSignOut, onUserUpdate }) {
  const [activeNav, setActiveNav] = useState('overview');
  const [admins, setAdmins] = useState([]);
  const [adminsPagination, setAdminsPagination] = useState({
    page: 1,
    pageSize: ADMINS_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [adminPage, setAdminPage] = useState(1);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [directoryStats, setDirectoryStats] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [updatingAdminId, setUpdatingAdminId] = useState('');
  const [adminActionMessage, setAdminActionMessage] = useState({
    type: 'idle',
    message: '',
  });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsAdminId, setAnalyticsAdminId] = useState('overall');
  const [analyticsStatus, setAnalyticsStatus] = useState({
    type: 'idle',
    message: '',
  });
  const [status, setStatus] = useState({
    type: 'loading',
    message: 'Loading admin records...',
  });

  const loadAdmins = useCallback(async () => {
    setStatus({ type: 'loading', message: 'Loading admin records...' });

    try {
      const payload = await getAdmins(token, {
        page: String(adminPage),
        pageSize: String(ADMINS_PAGE_SIZE),
        search: search.trim() || undefined,
      });
      setAdmins(payload.admins || []);
      setAdminsPagination(
        payload.pagination || {
          page: adminPage,
          pageSize: ADMINS_PAGE_SIZE,
          total: (payload.admins || []).length,
          totalPages: 1,
        }
      );
      if (payload.stats) {
        setDirectoryStats(payload.stats);
      }
      setStatus({ type: 'ready', message: '' });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to load admin records.',
      });
    }
  }, [token, adminPage, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = query.trim();
      setSearch((current) => {
        if (current === next) {
          return current;
        }
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setAdminPage(1);
  }, [search]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const loadPersonalizedSummary = useCallback(
    (days) =>
      getSuperAdminPersonalizedAnalytics(token, {
        adminId: analyticsAdminId === 'overall' ? null : analyticsAdminId,
        days,
      }),
    [token, analyticsAdminId]
  );

  useEffect(() => {
    if (activeNav !== 'analytics') {
      return undefined;
    }

    let isMounted = true;
    setAnalyticsStatus({ type: 'loading', message: 'Loading admin list...' });

    getSuperAdminAnalytics(token, { page: 1, pageSize: 100 })
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setAnalytics(payload.summary);
        setAnalyticsStatus({ type: 'ready', message: '' });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setAnalyticsStatus({
          type: 'error',
          message: error.message || 'Unable to load analytics admins.',
        });
      });

    return () => {
      isMounted = false;
    };
  }, [activeNav, token]);

  const analyticsAdminOptions = useMemo(() => {
    if (analytics?.admins?.length) {
      return analytics.admins.map((row) => ({
        id: row.adminId,
        name: row.name,
        email: row.email,
      }));
    }

    return admins.map((admin) => ({
      id: admin.id,
      name: admin.name,
      email: admin.email,
    }));
  }, [admins, analytics?.admins]);

  const analyticsScopeLabel = useMemo(() => {
    if (analyticsAdminId === 'overall') {
      return 'Overall platform';
    }

    const selected = analyticsAdminOptions.find((admin) => admin.id === analyticsAdminId);
    return selected?.name || 'Selected admin';
  }, [analyticsAdminId, analyticsAdminOptions]);

  const adminsByLastActive = useMemo(
    () =>
      [...admins].sort((left, right) => {
        const leftTime = left.lastActiveAt ? new Date(left.lastActiveAt).getTime() : 0;
        const rightTime = right.lastActiveAt ? new Date(right.lastActiveAt).getTime() : 0;
        return rightTime - leftTime;
      }),
    [admins]
  );

  const stats = directoryStats || {
    total: 0,
    spApiConnected: 0,
    spApiExpired: 0,
    spApiPending: 0,
    activeLast30Days: 0,
    inactiveLast30Days: 0,
  };

  const adminsRangeStart =
    adminsPagination.total === 0
      ? 0
      : (adminsPagination.page - 1) * adminsPagination.pageSize + 1;
  const adminsRangeEnd = Math.min(
    adminsPagination.page * adminsPagination.pageSize,
    adminsPagination.total
  );

  const navItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: IconHome,
      active: activeNav === 'overview',
      onClick: () => setActiveNav('overview'),
    },
    {
      id: 'admins',
      label: 'Admin accounts',
      icon: IconUser,
      active: activeNav === 'admins',
      onClick: () => setActiveNav('admins'),
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: IconChart,
      active: activeNav === 'analytics',
      onClick: () => setActiveNav('analytics'),
    },
  ];

  const toggleAdminStatus = async (admin) => {
    if (!admin || updatingAdminId) {
      return;
    }

    const nextIsActive = !admin.isActive;
    const action = nextIsActive ? 'unblock' : 'block';

    setUpdatingAdminId(admin.id);
    setAdminActionMessage({ type: 'idle', message: '' });

    try {
      const payload = await updateAdminStatus(token, admin.id, nextIsActive);
      setAdmins((current) =>
        current.map((item) => (item.id === admin.id ? payload.admin : item))
      );
      setSelectedAdmin((current) =>
        current?.id === admin.id ? payload.admin : current
      );
      setAdminActionMessage({
        type: 'success',
        message: payload.message || `Admin ${nextIsActive ? 'unblocked' : 'blocked'}.`,
      });
    } catch (error) {
      setAdminActionMessage({
        type: 'error',
        message: error.message || `Unable to ${action} admin.`,
      });
    } finally {
      setUpdatingAdminId('');
    }
  };

  const renderAdminRow = (admin) => (
    <tr
      key={admin.id}
      className="border-t border-[rgb(var(--crm-border))] transition hover:bg-[rgb(var(--crm-accent-soft)/0.35)]"
    >
      <td className={tableCellClassName}>
        <div className="flex items-center gap-3">
          <span
            className={[
              'grid h-9 w-9 shrink-0 place-items-center rounded-lg text-xs font-bold',
              admin.isActive
                ? 'bg-[rgb(var(--crm-accent-soft))] text-[rgb(var(--crm-accent-text))]'
                : 'bg-slate-100 text-slate-500',
            ].join(' ')}
          >
            {admin.name
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toUpperCase()}
          </span>
          <div className="min-w-0 grid gap-0.5">
            <strong className="truncate font-semibold text-[rgb(var(--crm-text))]">
              {admin.name}
            </strong>
            <span className="truncate text-xs text-[rgb(var(--crm-text-muted))]">
              {admin.email}
            </span>
          </div>
        </div>
      </td>
      <td className={`${tableCellClassName} whitespace-nowrap text-[rgb(var(--crm-text))]`}>
        {admin.lastActiveAt ? (
          new Date(admin.lastActiveAt).toLocaleString()
        ) : (
          <span className="text-[rgb(var(--crm-text-muted))]">Never recorded</span>
        )}
      </td>
      <td className={tableCellClassName}>
        <AccountStatusBadge isActive={admin.isActive} />
      </td>
      <td className={tableCellClassName}>
        <button
          type="button"
          className="text-sm font-semibold text-[rgb(var(--crm-accent-text))] transition hover:underline"
          onClick={() => setSelectedAdmin(admin)}
        >
          View
        </button>
      </td>
    </tr>
  );

  return (
    <DashboardShell
      user={user}
      navItems={navItems}
      onSignOut={onSignOut}
      title={
        {
          overview: 'Overview',
          admins: 'Admin accounts',
          analytics: 'Analytics',
        }[activeNav]
      }
      subtitle={
        {
          overview: 'System summary for super admin operations.',
          admins: 'Manage registered seller admins, access status, and account details.',
          analytics: 'Platform-wide or per-admin analytics dashboard.',
        }[activeNav]
      }
      settingsProps={{ user, token, onUserUpdate }}
    >
      <StatusBanner
        status={status.type === 'error' ? status : { type: 'idle', message: '' }}
        setStatus={setStatus}
      />

      {activeNav === 'overview' ? (
        <section className="grid gap-5">
          <div className="relative overflow-hidden rounded-2xl border border-sky-200/70 bg-[linear-gradient(135deg,#ffffff_0%,rgb(14_165_233/0.1)_45%,rgb(16_185_129/0.1)_100%)] p-5 shadow-sm sm:p-6">
            <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-sky-300/20 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-16 left-1/3 h-36 w-36 rounded-full bg-emerald-300/15 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">
                Platform control
              </p>
              <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                Operations overview
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
                Live snapshot of admin accounts, SP-API health, and recent activity across the seller workspace.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <OverviewMetricCard
              label="Total admins"
              value={status.type === 'loading' ? '…' : stats.total}
              detail="Registered admin accounts in the CRM database"
              icon={IconUser}
              tone="blue"
            />
            <OverviewMetricCard
              label="SP-API connected"
              value={status.type === 'loading' ? '…' : stats.spApiConnected}
              detail="Admins who completed SP-API credential setup"
              icon={IconLink}
              tone="emerald"
            />
            <OverviewMetricCard
              label="Pending SP-API"
              value={status.type === 'loading' ? '…' : stats.spApiPending}
              detail="Admins still waiting for initial credential setup"
              icon={IconKey}
              tone="amber"
            />
            <OverviewMetricCard
              label="Expired SP-API"
              value={status.type === 'loading' ? '…' : stats.spApiExpired}
              detail="Connected credentials that require renewal"
              icon={IconShield}
              tone={stats.spApiExpired ? 'rose' : 'emerald'}
            />
            <OverviewMetricCard
              label="Active in last 30 days"
              value={status.type === 'loading' ? '…' : stats.activeLast30Days}
              detail="Admins who signed in or restored a session"
              icon={IconChart}
              tone="violet"
            />
            <OverviewMetricCard
              label="Inactive in last 30 days"
              value={status.type === 'loading' ? '…' : stats.inactiveLast30Days}
              detail="Admins with no activity during this period"
              icon={IconClock}
              tone={stats.inactiveLast30Days ? 'slate' : 'emerald'}
            />
          </div>

          <div className={`${panelClassName} overflow-hidden`}>
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-sky-100 bg-[linear-gradient(135deg,#ffffff,rgb(14_165_233/0.06))] px-5 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">Directory</p>
                <h2 className="mt-1 text-base font-semibold text-slate-900">Admin last activity</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Latest page of admins (paged — never loads every account at once).
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse">
                <thead>
                  <tr>
                    {['Name', 'Email', 'Account status', 'Last active'].map((column) => (
                      <th key={column} className={tableHeadClassName}>
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {status.type === 'loading' ? (
                    <tr>
                      <td
                        colSpan={4}
                        className={`${tableCellClassName} text-center text-slate-500`}
                      >
                        Loading activity…
                      </td>
                    </tr>
                  ) : adminsByLastActive.length ? (
                    adminsByLastActive.map((admin) => (
                      <tr
                        key={admin.id}
                        className="border-t border-slate-100 transition hover:bg-sky-50/50"
                      >
                        <td className={tableCellClassName}>
                          <div className="flex items-center gap-3">
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-100 text-xs font-bold text-sky-700">
                              {admin.name
                                .split(/\s+/)
                                .slice(0, 2)
                                .map((part) => part[0])
                                .join('')
                                .toUpperCase()}
                            </span>
                            <strong className="font-semibold text-slate-900">{admin.name}</strong>
                          </div>
                        </td>
                        <td className={`${tableCellClassName} text-slate-600`}>{admin.email}</td>
                        <td className={tableCellClassName}>
                          <AccountStatusBadge isActive={admin.isActive} />
                        </td>
                        <td className={`${tableCellClassName} text-slate-600`}>
                          {admin.lastActiveAt
                            ? new Date(admin.lastActiveAt).toLocaleString()
                            : 'Never recorded'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className={`${tableCellClassName} text-center text-slate-500`}
                      >
                        No admin accounts registered yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {activeNav === 'admins' ? (
        <section className="grid gap-5">
          {adminActionMessage.message ? (
            <p
              className={[
                'rounded-xl border px-4 py-3 text-sm',
                adminActionMessage.type === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-red-200 bg-red-50 text-red-800',
              ].join(' ')}
            >
              {adminActionMessage.message}
            </p>
          ) : null}

          <div className={`${panelClassName} overflow-hidden`}>
            <div className="flex flex-wrap items-end justify-between gap-5 border-b border-[rgb(var(--crm-border))] bg-[linear-gradient(135deg,rgb(var(--crm-surface)),rgb(var(--crm-accent-soft)/0.45))] px-5 py-5">
              <div className="min-w-0 max-w-xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--crm-accent))]">
                  Directory
                </p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-[rgb(var(--crm-text))]">
                  Admin directory
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-[rgb(var(--crm-text-muted))]">
                  Search accounts, check recent activity, and open details to manage access.
                </p>
              </div>
              <div className="flex flex-wrap gap-6 sm:gap-8">
                {[
                  {
                    label: 'Registered',
                    value: status.type === 'loading' ? '…' : stats.total,
                  },
                  {
                    label: 'Active · 30d',
                    value: status.type === 'loading' ? '…' : stats.activeLast30Days,
                  },
                  {
                    label: 'SP-API linked',
                    value: status.type === 'loading' ? '…' : stats.spApiConnected,
                  },
                ].map((item) => (
                  <div key={item.label} className="min-w-[4.5rem]">
                    <span className="block text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--crm-text-muted))]">
                      {item.label}
                    </span>
                    <strong className="mt-1 block text-2xl font-semibold tracking-tight text-[rgb(var(--crm-text))]">
                      {item.value}
                    </strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgb(var(--crm-border))] px-5 py-4">
              <label className="w-full max-w-md flex-1">
                <span className="sr-only">Search admins</span>
                <input
                  className={inputClassName}
                  type="search"
                  placeholder="Search by name, email, or phone"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
              <span className="text-sm text-[rgb(var(--crm-text-muted))]">
                {status.type === 'loading' ? (
                  'Loading…'
                ) : (
                  <>
                    Showing{' '}
                    <span className="font-medium text-[rgb(var(--crm-text))]">
                      {adminsRangeStart}–{adminsRangeEnd}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium text-[rgb(var(--crm-text))]">
                      {adminsPagination.total}
                    </span>
                  </>
                )}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse">
                <thead>
                  <tr className="bg-[rgb(var(--crm-page-bg))]">
                    {['Name', 'Last active', 'Status', ''].map((column) => (
                      <th
                        key={column || 'actions'}
                        className={`${tableHeadClassName} ${column === '' ? 'w-[6rem]' : ''}`}
                      >
                        {column || <span className="sr-only">Actions</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {status.type === 'loading' ? (
                    <tr>
                      <td
                        colSpan={4}
                        className={`${tableCellClassName} py-12 text-center text-[rgb(var(--crm-text-muted))]`}
                      >
                        Loading records…
                      </td>
                    </tr>
                  ) : admins.length ? (
                    admins.map((admin) => renderAdminRow(admin))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className={`${tableCellClassName} py-12 text-center text-[rgb(var(--crm-text-muted))]`}
                      >
                        {search
                          ? 'No admins match your search.'
                          : 'No admin accounts registered yet.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {adminsPagination.totalPages > 1 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-5 py-3">
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={adminPage <= 1 || status.type === 'loading'}
                  onClick={() => setAdminPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span className="min-w-[7rem] text-center text-xs font-semibold text-[rgb(var(--crm-text))]">
                  Page {adminsPagination.page} of {adminsPagination.totalPages}
                </span>
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={
                    adminPage >= adminsPagination.totalPages || status.type === 'loading'
                  }
                  onClick={() => setAdminPage((current) => current + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {activeNav === 'analytics' ? (
        <section className="grid gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Analytics scope</h2>
              <p className="mt-1 text-sm text-slate-500">
                {analyticsAdminId === 'overall'
                  ? 'Platform-wide dashboard across all seller admins.'
                  : `Viewing personalized analytics for ${analyticsScopeLabel}.`}
              </p>
            </div>
            <label className="grid min-w-[14rem] w-full max-w-sm gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Admin filter
              </span>
              <select
                className={inputClassName}
                value={analyticsAdminId}
                onChange={(event) => setAnalyticsAdminId(event.target.value)}
              >
                <option value="overall">Overall</option>
                {analyticsAdminOptions.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name}
                  </option>
                ))}
                {analyticsAdminId !== 'overall' &&
                !analyticsAdminOptions.some((admin) => admin.id === analyticsAdminId) ? (
                  <option value={analyticsAdminId}>Selected admin</option>
                ) : null}
              </select>
            </label>
          </div>

          <StatusBanner
            status={
              analyticsStatus.type === 'error'
                ? analyticsStatus
                : { type: 'idle', message: '' }
            }
            setStatus={setAnalyticsStatus}
          />

          <AnalyticsPage
            key={analyticsAdminId}
            token={token}
            loadSummary={loadPersonalizedSummary}
            scopeLabel={
              analyticsAdminId === 'overall'
                ? 'all seller admins'
                : analyticsScopeLabel
            }
          />
        </section>
      ) : null}

      <AdminDetailsModal
        admin={selectedAdmin}
        onClose={() => setSelectedAdmin(null)}
        onStatusChange={toggleAdminStatus}
        updating={Boolean(updatingAdminId)}
      />
    </DashboardShell>
  );
}

function Dashboard({ user, token, onSignOut, onUserUpdate }) {
  if (user.role === 'super_admin') {
    return (
      <SuperAdminDashboard
        token={token}
        user={user}
        onSignOut={onSignOut}
        onUserUpdate={onUserUpdate}
      />
    );
  }

  return null;
}

export default Dashboard;
